import React, { FC, useState } from "react";
import { CompleteTaskModal } from "shared/ui";
import { TaskCalendarHeader } from "./calendar/components/TaskCalendarHeader";
import { TaskGanttPanel } from "./calendar/components/TaskGanttPanel";
import { TaskScheduleCalendar } from "./calendar/components/TaskScheduleCalendar";
import { useTaskCalendarEvents } from "./calendar/hooks/useTaskCalendarEvents";
import { useTaskCalendarState } from "./calendar/hooks/useTaskCalendarState";
import type { TaskCalendarProps } from "./calendar/types";

export type { CalendarMode } from "./calendar/types";
export { TaskCalendarStatus as Statuses } from "./calendar/types";

const TaskCalendar: FC<TaskCalendarProps> = ({
  onBackClick,
  mode = "executions",
}) => {
  const {
    t,
    viewMode,
    currentPeriod,
    anchorDate,
    range,
    viewModeOptions,
    ganttPeriodBounds,
    handleViewModeChange,
    handleScheduleDatesChange,
  } = useTaskCalendarState();

  const { events } = useTaskCalendarEvents({ mode, range, t });

  // Event ids are task execution ids in "executions" mode; standalone alarms
  // can't be completed, so they stay non-clickable.
  const [selectedExecutionId, setSelectedExecutionId] = useState<number | null>(
    null
  );
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  const handleEventClick =
    mode === "executions"
      ? (eventId: string) => {
          setSelectedExecutionId(Number(eventId));
          setIsCompleteModalOpen(true);
        }
      : undefined;

  return (
    <div className="w-full flex flex-col bg-white text-black overflow-hidden">
      <TaskCalendarHeader
        onBackClick={onBackClick}
        t={t}
        viewMode={viewMode}
        viewModeOptions={viewModeOptions}
        onViewModeChange={handleViewModeChange}
      />

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {viewMode === "schedule" ? (
          <TaskScheduleCalendar
            events={events}
            t={t}
            fullHeight={Boolean(onBackClick)}
            onDatesChange={handleScheduleDatesChange}
            onEventClick={handleEventClick}
          />
        ) : (
          <TaskGanttPanel
            t={t}
            events={events}
            currentPeriod={currentPeriod}
            anchorDate={anchorDate}
            rangeStart={ganttPeriodBounds.start}
            rangeEnd={ganttPeriodBounds.end}
            onDatesChange={handleScheduleDatesChange}
          />
        )}
      </div>

      {/*
       * Saving invalidates the executions LIST tag, so the calendar refetches
       * and the event picks up its new status color on its own.
       */}
      <CompleteTaskModal
        open={isCompleteModalOpen}
        taskExecutionId={selectedExecutionId}
        onClose={() => setIsCompleteModalOpen(false)}
      />
    </div>
  );
};

export default TaskCalendar;
